import React, { useState, useEffect, useContext } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { AuthContext } from '../../../contexts/AuthContext';
import { bulkOperationsService } from '../../../services/bulkOperationsService';

const BulkUpdateTools = () => {
  const { user, userProfile } = useContext(AuthContext);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [formData, setFormData] = useState({
    operation_name: '',
    description: '',
    operation_type: 'account_update',
    target_table: 'chart_of_accounts'
  });

  const [filterCriteria, setFilterCriteria] = useState({
    account_type: '',
    account_subtype: '',
    status: '',
    account_codes: ''
  });

  const [updateValues, setUpdateValues] = useState({
    account_subtype: '',
    status: '',
    allows_posting: ''
  });

  const [operationTypes, setOperationTypes] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [accountSubtypes, setAccountSubtypes] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    if (userProfile?.organizations?.[0]?.id) {
      loadOperations();
      loadFormOptions();
    }
  }, [userProfile]);

  const loadFormOptions = async () => {
    try {
      // Load dynamic options from the database or service
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      // Load operation types from system configuration
      const operationTypesResult = await bulkOperationsService?.getOperationTypes?.(organizationId);
      if (operationTypesResult?.data) {
        setOperationTypes(operationTypesResult?.data);
      } else {
        // Fallback to minimal options if service not available
        setOperationTypes([
          { value: 'account_update', label: 'Account Update' }
        ]);
      }

      // Load account types from chart of accounts
      const accountTypesResult = await bulkOperationsService?.getAccountTypes?.(organizationId);
      if (accountTypesResult?.data) {
        setAccountTypes([
          { value: '', label: 'All Account Types' },
          ...accountTypesResult?.data
        ]);
      } else {
        setAccountTypes([{ value: '', label: 'All Account Types' }]);
      }

      // Load account subtypes
      const subtypesResult = await bulkOperationsService?.getAccountSubtypes?.(organizationId);
      if (subtypesResult?.data) {
        setAccountSubtypes([
          { value: '', label: 'No Change' },
          ...subtypesResult?.data
        ]);
      } else {
        setAccountSubtypes([{ value: '', label: 'No Change' }]);
      }

      // Load status options
      setStatusOptions([
        { value: '', label: 'No Change' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]);

    } catch (error) {
      console.error('Error loading form options:', error);
      // Set minimal fallback options
      setOperationTypes([{ value: 'account_update', label: 'Account Update' }]);
      setAccountTypes([{ value: '', label: 'All Account Types' }]);
      setAccountSubtypes([{ value: '', label: 'No Change' }]);
      setStatusOptions([
        { value: '', label: 'No Change' },
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ]);
    }
  };

  const loadOperations = async () => {
    setLoading(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      const result = await bulkOperationsService?.getBulkOperations(organizationId, {
        page: 1,
        limit: 50
      });

      if (result?.data) {
        setOperations(result?.data);
      }
    } catch (error) {
      console.error('Load operations error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!userProfile?.organizations?.[0]?.id) return;

    setPreviewLoading(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      const result = await bulkOperationsService?.previewBulkOperation(
        organizationId,
        formData?.target_table,
        filterCriteria
      );

      if (result?.error) {
        alert('Preview error: ' + result?.error);
        return;
      }

      setPreviewData(result);
    } catch (error) {
      console.error('Preview error:', error);
      alert('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!user?.id || !userProfile?.organizations?.[0]?.id) return;

    setCreating(true);
    try {
      const organizationId = userProfile?.organizations?.[0]?.id;
      
      // Create bulk operation record
      const operationResult = await bulkOperationsService?.createBulkOperation({
        organization_id: organizationId,
        created_by: user?.id,
        ...formData,
        filter_criteria: filterCriteria,
        update_values: Object.fromEntries(
          Object.entries(updateValues)?.filter(([_, value]) => value !== '')
        ),
        records_to_process: previewData?.totalCount || 0
      });

      if (operationResult?.error) {
        alert('Error creating operation: ' + operationResult?.error);
        return;
      }

      // Execute the bulk operation
      const executionResult = await bulkOperationsService?.executeBulkAccountUpdate(
        operationResult?.data?.id,
        organizationId,
        filterCriteria,
        Object.fromEntries(
          Object.entries(updateValues)?.filter(([_, value]) => value !== '')
        )
      );

      if (executionResult?.error) {
        alert('Error executing operation: ' + executionResult?.error);
        return;
      }

      // Reset form
      setFormData({
        operation_name: '',
        description: '',
        operation_type: 'account_update',
        target_table: 'chart_of_accounts'
      });
      setFilterCriteria({
        account_type: '',
        account_subtype: '',
        status: '',
        account_codes: ''
      });
      setUpdateValues({
        account_subtype: '',
        status: '',
        allows_posting: ''
      });
      setPreviewData(null);
      setShowForm(false);

      // Reload operations
      await loadOperations();

      alert(
        `Bulk operation completed!\n` +
        `Records processed: ${executionResult?.recordsProcessed}\n` +
        `Successful: ${executionResult?.recordsSucceeded}\n` +
        `Failed: ${executionResult?.recordsFailed}`
      );
    } catch (error) {
      console.error('Submit error:', error);
      alert('Failed to create bulk operation');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors?.[status] || colors?.pending}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  const getProgressPercentage = (operation) => {
    if (operation?.records_to_process === 0) return 0;
    return Math.round((operation?.records_processed / operation?.records_to_process) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Bulk Update Tools</h2>
          <p className="text-muted-foreground mt-1">Mass data modifications with preview capabilities and progress tracking</p>
        </div>
        <Button 
          onClick={() => setShowForm(!showForm)}
          iconName="Plus"
          iconPosition="left"
          className="mt-4 sm:mt-0"
        >
          New Bulk Operation
        </Button>
      </div>
      {/* Bulk Operation Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">New Bulk Operation</h3>
          
          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Input
              label="Operation Name"
              value={formData?.operation_name}
              onChange={(e) => setFormData({...formData, operation_name: e?.target?.value})}
              placeholder="Enter operation name"
              required
            />
            <Select
              label="Operation Type"
              value={formData?.operation_type}
              onChange={(value) => setFormData({...formData, operation_type: value})}
              options={operationTypes}
              required
            />
          </div>

          <div className="mb-6">
            <Input
              label="Description"
              value={formData?.description}
              onChange={(e) => setFormData({...formData, description: e?.target?.value})}
              placeholder="Enter operation description"
            />
          </div>

          {/* Filter Criteria */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-foreground mb-3">Filter Criteria</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select
                label="Account Type"
                value={filterCriteria?.account_type}
                onChange={(value) => setFilterCriteria({...filterCriteria, account_type: value})}
                options={accountTypes}
              />
              <Select
                label="Account Subtype"
                value={filterCriteria?.account_subtype}
                onChange={(value) => setFilterCriteria({...filterCriteria, account_subtype: value})}
                options={accountSubtypes?.filter(opt => opt?.value !== '')}
                placeholder="Any Subtype"
              />
              <Select
                label="Status"
                value={filterCriteria?.status}
                onChange={(value) => setFilterCriteria({...filterCriteria, status: value})}
                options={[
                  { value: '', label: 'Any Status' },
                  { value: 'active', label: 'Active' },
                  { value: 'inactive', label: 'Inactive' }
                ]}
              />
            </div>
            <div className="mt-4">
              <Input
                label="Account Codes (comma-separated)"
                value={filterCriteria?.account_codes}
                onChange={(e) => setFilterCriteria({...filterCriteria, account_codes: e?.target?.value})}
                placeholder="Enter specific account codes (optional)"
              />
            </div>
          </div>

          {/* Update Values */}
          <div className="mb-6">
            <h4 className="text-md font-medium text-foreground mb-3">Update Values</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Select
                label="New Account Subtype"
                value={updateValues?.account_subtype}
                onChange={(value) => setUpdateValues({...updateValues, account_subtype: value})}
                options={accountSubtypes}
              />
              <Select
                label="New Status"
                value={updateValues?.status}
                onChange={(value) => setUpdateValues({...updateValues, status: value})}
                options={statusOptions}
              />
              <Select
                label="Allows Posting"
                value={updateValues?.allows_posting}
                onChange={(value) => setUpdateValues({...updateValues, allows_posting: value})}
                options={[
                  { value: '', label: 'No Change' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' }
                ]}
              />
            </div>
          </div>

          {/* Preview Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-md font-medium text-foreground">Preview</h4>
              <Button
                type="button"
                onClick={handlePreview}
                variant="outline"
                size="sm"
                disabled={previewLoading}
                iconName={previewLoading ? "Loader2" : "Eye"}
                className={previewLoading ? "animate-spin" : ""}
              >
                {previewLoading ? 'Loading...' : 'Preview'}
              </Button>
            </div>

            {previewData && (
              <div className="bg-muted/20 border border-border rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-3">
                  <strong>{previewData?.totalCount}</strong> records will be affected by this operation
                </p>
                
                {previewData?.data?.length > 0 && (
                  <div className="max-h-64 overflow-y-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2">Code</th>
                          <th className="text-left py-2">Name</th>
                          <th className="text-left py-2">Type</th>
                          <th className="text-left py-2">Subtype</th>
                          <th className="text-left py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewData?.data?.map((account) => (
                          <tr key={account?.id} className="border-b border-border">
                            <td className="py-2">{account?.account_code}</td>
                            <td className="py-2">{account?.account_name}</td>
                            <td className="py-2">{account?.account_type}</td>
                            <td className="py-2">{account?.account_subtype}</td>
                            <td className="py-2">{account?.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              onClick={() => setShowForm(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || !previewData || previewData?.totalCount === 0}
              iconName={creating ? "Loader2" : "Play"}
              className={creating ? "animate-spin" : ""}
            >
              {creating ? 'Executing...' : 'Execute Operation'}
            </Button>
          </div>
        </form>
      )}
      {/* Operations List */}
      <div className="bg-card border border-border rounded-lg">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Bulk Operations History</h3>
        </div>
        
        {operations?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Database" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Bulk Operations</h3>
            <p className="text-muted-foreground">Create your first bulk operation to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {operations?.map((operation) => (
              <div key={operation?.id} className="px-6 py-4 hover:bg-muted/20">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-foreground">{operation?.operation_name}</span>
                      {getStatusBadge(operation?.status)}
                      <span className="text-sm text-muted-foreground">
                        {operation?.operation_type?.replace('_', ' ')?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-foreground mb-2">{operation?.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span>Created by {operation?.created_by?.full_name}</span>
                      <span>•</span>
                      <span>{new Date(operation?.created_at)?.toLocaleDateString()}</span>
                    </div>
                    
                    {/* Progress Bar */}
                    {operation?.status === 'processing' || operation?.status === 'completed' ? (
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Progress</span>
                          <span>{getProgressPercentage(operation)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full transition-all duration-300"
                            style={{ width: `${getProgressPercentage(operation)}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>Processed: {operation?.records_processed}</span>
                          <span>Success: {operation?.records_succeeded}</span>
                          <span>Failed: {operation?.records_failed}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkUpdateTools;